/**
 * CSV builder for the Extra Locale admin export — one row per school:
 *
 *   School Name, Subscription, Selected Extra LOCALEs, Maximum,
 *   Selected Extra LOCALE Name 1, Selected Extra LOCALE Name 2, ...
 *
 * Rows come from GET /api/admin/extraLocale/schoolSummary (every school an
 * active plan makes eligible, plus schools holding selections after a plan
 * lapsed). EACH selected name gets its OWN COLUMN (2026-08-27 revision — the
 * pipe-joined single cell crammed everything into one Excel column), carrying
 * WHO picked it:
 *
 *   GS01 [TC] (26/27) 大夫第 (Selected by 陳大文)
 *
 * SUBSCRIPTION column (2026-08-28 revision): a selection is keyed by
 * SUBSCRIPTION, not by school — it is a `subscription_epack` row hanging off
 * the school's granting subscription (its active PLAN_EXTRA_LOCALE plan with
 * the highest value). Schools sharing one subscription therefore share ONE
 * allowance, so they export the SAME quota and name that plan here: QEF 2024
 * Sec is held by 29 schools (34 grant rows — five schools hold it twice).
 * Without this column those 29 near-identical rows read as duplicated data or
 * an export bug; with it the repetition explains itself — same subscription,
 * same pool.
 *
 * Their `used` and name columns are NOT necessarily identical, and the server
 * stopped claiming they were: `school_selection_summary` reads `used` and
 * `selections` PER SCHOOL, across every subscription that school holds, so a
 * school on the shared plan AND a second plan exports the union. Only `quota`
 * and the Subscription cell are per-plan. An older server that predates the
 * re-key sends no subscription_id/subscription_name and the cell is left empty.
 *
 * Names are the selections in pick order, and the header carries as many
 * numbered name columns as the widest row needs. The server sends the
 * selector in `selections`; `names` (flat strings) is the fallback for an
 * older server that predates it, which yields the same cells minus the
 * suffix. Pure string-in/string-out so it is unit-testable; the caller
 * prepends the UTF-8 BOM when handing the string to a Blob (Excel reads
 * un-BOM'd UTF-8 CSV as its locale codepage and mangles Chinese).
 */

export const EXTRA_LOCALE_CSV_FIXED_HEADERS = [
  "School Name",
  "Subscription",
  "Selected Extra LOCALEs",
  "Maximum",
];

export const EXTRA_LOCALE_CSV_NAME_HEADER = "Selected Extra LOCALE Name";
export const EXTRA_LOCALE_CSV_SELECTED_BY = "Selected by";

/**
 * Excel/Sheets execute a cell whose text begins with = + - @ (or a control
 * char), so a LOCALE named "=cmd|..." — a name any teacher can author — would
 * run as a formula when a CLST admin opens this export. Neutralise by leading
 * with an apostrophe, which those apps consume as "treat the rest as text".
 *
 * Applied ONLY to cells that would otherwise be parsed as a formula: no value
 * in the live data starts with one of these (verified across every epack,
 * school and user name), so today this rewrites nothing.
 */
function neutralizeFormula(text) {
  return /^[=+\-@\t\r]/.test(text) ? "'" + text : text;
}

/**
 * RFC-4180 quoting: wrap when the value carries a comma, quote or newline.
 * Exported so the Selection History export (selectionHistoryCsv.js) escapes
 * and formula-guards its cells by exactly the same rule.
 */
export function csvField(value) {
  const text = neutralizeFormula(String(value == null ? "" : value));
  if (/[",\r\n]/.test(text)) {
    return '"' + text.replace(/"/g, '""') + '"';
  }
  return text;
}

export function schoolLabel(row) {
  return row.school_name || row.school_name_eng || ('#' + row.school_id);
}

/**
 * The granting subscription a school's selections actually live on: its name,
 * else '#<id>' when the row carries an id but no name (an unnamed plan still
 * has to be distinguishable, because the whole point of the column is telling
 * two schools' pools apart).
 *
 * "" — not "#undefined" — when there is no id at all: schoolSummary rows from
 * a server predating the subscription re-key have neither key, and a school
 * with no ACTIVE PLAN_EXTRA_LOCALE plan has no granting subscription to name.
 * Hence the explicit finite/positive check rather than schoolLabel()'s plain
 * `||` chain, whose last term is unconditional.
 */
export function subscriptionLabel(row) {
  const source = row || {};
  if (source.subscription_name) return String(source.subscription_name);
  const id = Number(source.subscription_id);
  return (Number.isFinite(id) && id > 0) ? ('#' + id) : "";
}

/**
 * Who picked a selection: display name, else username, else the bare user id
 * (the account may have been deleted — the id still identifies the row in the
 * ledger). "" only when the row carries no identity at all, and the caller
 * then writes the LOCALE name with no suffix rather than "(Selected by )".
 */
export function selectorLabel(selection) {
  if (!selection || typeof selection !== 'object') {
    return "";
  }
  if (selection.user_display_name) return String(selection.user_display_name);
  if (selection.username) return String(selection.username);
  const id = Number(selection.user_id);
  return (Number.isFinite(id) && id > 0) ? ('#' + id) : "";
}

/** One cell per selection: "<LOCALE name> (Selected by <who>)". */
export function selectionCells(row) {
  const source = row || {};
  if (Array.isArray(source.selections) && source.selections.length > 0) {
    return source.selections.map((selection) => {
      const name = String((selection && selection.name) || "");
      const who = selectorLabel(selection);
      return who ? `${name} (${EXTRA_LOCALE_CSV_SELECTED_BY} ${who})` : name;
    });
  }
  // Older server (no `selections`): names only, no selector suffix.
  return Array.isArray(source.names) ? source.names.map((n) => String(n == null ? "" : n)) : [];
}

export function buildExtraLocaleCsv(rows) {
  const list = Array.isArray(rows) ? rows : [];
  const cellsPerRow = list.map((row) => selectionCells(row));
  // Every row is padded to the widest selection count, so the sheet stays
  // rectangular and each name column has a header. At least one name column
  // even when nothing is selected anywhere, so the header shape is stable.
  const nameColumns = Math.max(1, ...cellsPerRow.map((cells) => cells.length));
  const headers = EXTRA_LOCALE_CSV_FIXED_HEADERS.concat(
    Array.from({ length: nameColumns },
      (_, i) => `${EXTRA_LOCALE_CSV_NAME_HEADER} ${i + 1}`)
  );
  const lines = [headers.map(csvField).join(",")];
  list.forEach((row, rowIndex) => {
    const names = cellsPerRow[rowIndex];
    const cells = [
      csvField(schoolLabel(row)),
      // Same csvField() pass as every other cell: a subscription name is admin
      // free text, so it can carry a comma ("QEF ... (2024 Pri/Sec), Phase 2")
      // or a leading '=' just as easily as a school or LOCALE name.
      csvField(subscriptionLabel(row)),
      // TWO COLUMNS, TWO BARE NUMBERS — never one "used / quota" string.
      //
      // This used to emit a single cell reading `2 / 9`, and Excel type-infers
      // that as a DATE: the column displayed 02/09 instead of the count
      // (reported 2026-08-30). Nothing about quoting fixes it — Excel coerces
      // after parsing the field, so `"2 / 9"` lands as a date too — and the
      // leading-apostrophe trick neutralizeFormula uses is not reliable on CSV
      // IMPORT: many Excel versions show the apostrophe literally, which would
      // corrupt a number a human is meant to read. Any single-cell "a / b" is
      // one locale setting away from the same bug.
      //
      // Two bare integers cannot be coerced into anything, and the column was
      // already called "Selected Extra LOCALEs / Maximum" — i.e. it always
      // described two things. It also makes the sheet more useful: an admin can
      // now sort by usage, and filter for schools at capacity, which a text cell
      // never allowed.
      csvField(row.used != null ? row.used : names.length),
      csvField(row.quota != null ? row.quota : 0),
    ];
    for (let i = 0; i < nameColumns; i += 1) {
      cells.push(csvField(names[i] != null ? names[i] : ""));
    }
    lines.push(cells.join(","));
  });
  // CRLF: the joiner most spreadsheet importers assume for CSV.
  return lines.join("\r\n") + "\r\n";
}

export default buildExtraLocaleCsv;
